
import { useState, useEffect, useRef, useCallback } from 'react';
import { MTCAPTCHA_CONFIG } from '@/config/mtcaptcha';
import { loadMTCaptcha } from '@/utils/deferredScripts';


export const useMTCaptcha = (isOpen: boolean) => {
  const [captchaToken, setCaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0); // Force re-render
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaRendered = useRef(false);
  const callbackId = useRef<string>('');
  const initializationStarted = useRef(false);

  // Fonction pour nettoyer le captcha - Sans manipulation DOM directe
  const cleanupCaptcha = useCallback(() => {
    //console.log('🧹 Cleaning up MTCaptcha...');
    setCaptchaToken('');
    setError('');
    setIsLoading(false);
    captchaRendered.current = false;
    initializationStarted.current = false;

    // Nettoyer les callbacks globaux uniquement
    if (callbackId.current) {
      delete (window as any)[`mtcaptcha_success_${callbackId.current}`];
      delete (window as any)[`mtcaptcha_expired_${callbackId.current}`];
      delete (window as any)[`mtcaptcha_error_${callbackId.current}`];
      callbackId.current = '';
    }
    
    // Force re-render instead of DOM manipulation
    setCaptchaKey(prev => prev + 1);
  }, []);

  // Fonction pour rendre le captcha - Sans manipulation DOM directe 
  const renderCaptcha = useCallback(() => {
    if (!captchaContainerRef.current || captchaRendered.current || !isOpen || initializationStarted.current) {
      return;
    }

    //console.log('🚀 Starting MTCaptcha render...');
    initializationStarted.current = true;
    setIsLoading(true);
    setError('');
    
    try {
      // Générer un ID unique pour les callbacks
      const uniqueId = Date.now().toString();
      callbackId.current = uniqueId;

      // Créer les callbacks globaux uniques qui mettent à jour le state React
      (window as any)[`mtcaptcha_success_${uniqueId}`] = (token: string) => {
        //console.log('✅ MTCaptcha SUCCESS callback triggered with token:', token?.substring(0, 20) + '...');
        setCaptchaToken(token);
        setError('');
        setIsLoading(false);
      };
      
      (window as any)[`mtcaptcha_expired_${uniqueId}`] = () => {
        //console.log('⚠️ MTCaptcha EXPIRED callback triggered');
        setCaptchaToken('');
        setError('Le captcha a expiré, veuillez le refaire');
        setIsLoading(false);
      };
      
      (window as any)[`mtcaptcha_error_${uniqueId}`] = (error: any) => {
        //console.log('❌ MTCaptcha ERROR callback triggered:', error);
        setCaptchaToken('');
        setError('Erreur lors du chargement du captcha');
        setIsLoading(false);
      };
      
      // Créer le div avec les attributs data-* selon la documentation
      const captchaDiv = document.createElement('div');
      captchaDiv.className = 'mtcaptcha';
      captchaDiv.setAttribute('data-sitekey', MTCAPTCHA_CONFIG.SITE_KEY);
      captchaDiv.setAttribute('data-theme', MTCAPTCHA_CONFIG.THEME);
      captchaDiv.setAttribute('data-size', MTCAPTCHA_CONFIG.SIZE);
      
      // Utiliser les callbacks uniques
      captchaDiv.setAttribute('data-callback', `mtcaptcha_success_${uniqueId}`);
      captchaDiv.setAttribute('data-expired-callback', `mtcaptcha_expired_${uniqueId}`);
      captchaDiv.setAttribute('data-error-callback', `mtcaptcha_error_${uniqueId}`);
      
      // Seulement ajouter si le conteneur est vide pour éviter les doublons
      if (captchaContainerRef.current && captchaContainerRef.current.children.length === 0) {
        captchaContainerRef.current.appendChild(captchaDiv);
      }
      
      //console.log('✅ MTCaptcha div created with unique callbacks:', uniqueId);
      captchaRendered.current = true;
      
      // Arrêter le loading après un délai court car le captcha va s'initialiser
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);

    } catch (initError) {
      console.error('❌ Error creating MTCaptcha:', initError);
      setError('Erreur lors du chargement du captcha');
      setIsLoading(false);
      initializationStarted.current = false;
    }
  }, [isOpen]);

  // Effet pour initialiser le captcha quand la modale s'ouvre
  useEffect(() => {
    if (isOpen && !captchaRendered.current && !initializationStarted.current) {
      // Filet de sécurité : force le chargement du script MTCaptcha
      // s'il n'a pas encore été déclenché par une interaction précédente.
      loadMTCaptcha();

      // Délai pour laisser le DOM se stabiliser
      const timeoutId = setTimeout(() => {
        renderCaptcha();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, renderCaptcha]);


  // Nettoyage quand la modale se ferme
  useEffect(() => {
    if (!isOpen) {
      cleanupCaptcha();
    }
  }, [isOpen, cleanupCaptcha]);

  // Fonction pour obtenir le token vérifié - Version améliorée
  const getVerifiedToken = useCallback((): string | null => {
    try {
      //console.log('🎯 getVerifiedToken() called');
      
      // Méthode 1: Vérifier d'abord s'il y a un input caché créé par MTCaptcha
      const hiddenInput = document.querySelector('input[name="mtcaptcha-verifiedtoken"]') as HTMLInputElement;
      if (hiddenInput && hiddenInput.value) {
        //console.log('✅ Token found in hidden input');
        return hiddenInput.value;
      }
      
      // Méthode 2: Utiliser l'API MTCaptcha si disponible
      if ((window as any).mtcaptcha && typeof (window as any).mtcaptcha.getVerifiedToken === 'function') {
        const token = (window as any).mtcaptcha.getVerifiedToken(MTCAPTCHA_CONFIG.SITE_KEY);
        if (token && token.trim()) {
          //console.log('✅ Valid token from MTCaptcha API');
          return token;
        }
      }
      
      // Méthode 3: Utiliser le token stocké dans le state React
      if (captchaToken && captchaToken.trim()) {
        //console.log('✅ Token from React state');
        return captchaToken;
      }
      
      console.warn('❌ No valid token found from any method');
      return null;
    } catch (error) {
      console.error('❌ Error in getVerifiedToken:', error);
      return null;
    }
  }, [captchaToken]);

  // Fonction pour réinitialiser le captcha
  const resetCaptcha = useCallback(() => {
    //console.log('🔄 Resetting MTCaptcha...');
    cleanupCaptcha();
    
    // Re-rendre le captcha après un délai
    if (isOpen) {
      setTimeout(() => renderCaptcha(), 1000);
    }
  }, [isOpen, renderCaptcha, cleanupCaptcha]);

  return {
    captchaContainerRef,
    captchaToken,
    error,
    isLoading,
    setError,
    getVerifiedToken,
    resetCaptcha,
    captchaKey // Pour forcer le re-render du composant
  };
};
